import { Injectable } from '@angular/core';
import { TenantDto, TenantsService } from '@careapp-group/ng-sdk';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, filter, map, switchMap, tap, withLatestFrom } from 'rxjs/operators';
import { CAAction, Organisations, Tenants } from '../actions';
import { getEntities, ORGANISATIONS } from '../data-list.reducer';
import { Store } from '@ngrx/store';

@Injectable()
export class TenantsEffects {
  requestTenants$ = createEffect((): any =>
    this.actions$.pipe(
      ofType(Tenants.RequestRetrieveAllAction),
      switchMap((action) =>
        this.tenantsService.getTenants({}).pipe(
          map((items: TenantDto[]) => Tenants.ResponseSuccessRetrieveAllAction({ payload: items })),
          catchError((error) => of(Tenants.ResponseFailRetrieveAllAction({ error }))),
        ),
      ),
    ),
  );
  createTenant$ = createEffect((): any =>
    this.actions$.pipe(
      ofType(Tenants.RequestCreateAction),
      switchMap((action) =>
        this.tenantsService.createTenant({ tenantRequestDto: action.payload }).pipe(
          map((response) => Tenants.ResponseSuccessCreateAction({ payload: response })),
          catchError((error) => of(Tenants.ResponseFailCreateAction({ error }))),
        ),
      ),
    ),
  );
  saveTenant$ = createEffect((): any =>
    this.actions$.pipe(
      ofType(Tenants.RequestUpdateAction),
      switchMap((action) =>
        this.tenantsService
          .updateTenant({ id: action.id, updateTenantRequestDto: action.data })
          .pipe(
            map((response) => Tenants.ResponseSuccessUpdateAction({ payload: response })),
            catchError((error) => of(Tenants.ResponseFailUpdateAction({ id: action.id, error }))),
          ),
      ),
    ),
  );

  saveTenantPlan$ = createEffect(() =>
    this.actions$.pipe(
      ofType(Tenants.requestSaveTenantPlan),
      switchMap((action) =>
        this.tenantsService
          .setTenantPlan({
            id: action.tenantId,
            tenantPlanDto: action.plan,
          })
          .pipe(
            map((response) =>
              Tenants.successSaveTenantPlan({ tenantId: action.tenantId, plan: response }),
            ),
            catchError((error) =>
              of(Tenants.failSaveTenantPlan({ tenantId: action.tenantId, error })),
            ),
          ),
      ),
    ),
  );

  /**
   * This makes sure the currently selected tenant matches the tenantId of the
   * currently selected org. This is because some routes, especially older admin
   * routes, do not have a tenantId in the path, and so the selected tenant
   * can become out of sync with the selected org.
   *
   * This causes issues with, for example, branding image uploads. Because the API
   * will throw an exception because the request's tenantId does not match
   * the org's ID.
   */
  keepSelectedTenantIdInSyncWithSelectedOrg$ = createEffect(() =>
    this.actions$.pipe(
      ofType(Organisations.SELECT_BY_ID),
      filter((action) => !!action.payload.id),
      withLatestFrom(this.store.select(getEntities(ORGANISATIONS))),
      map(([action, orgs]) => Tenants.SelectByIdAction({ id: orgs[action.payload.id].tenantId })),
    ),
  );

  constructor(
    private store: Store,
    private actions$: Actions<CAAction>,
    private tenantsService: TenantsService,
  ) {}
}
